import { SupabaseClient } from "@supabase/supabase-js";
import { OeeQuoteStatus } from "@/features/ava-add-quotes/_logic/constants";
import { Guestrooms, MeetingRooms } from "@/features/ava-add-quotes/_logic/types";

/**
 * The minimum a quote-like row needs for the ordering rules below. Kept structural
 * rather than tied to `OeeQuote` so surfaces that map quotes into their own shapes
 * first — the sourcing tracker's `QuoteRow` — can share the ordering instead of
 * inventing their own.
 */
export interface SortableQuote {
  id: number;
  created_at?: string | null;
  is_active?: boolean;
}

/**
 * Compares two quote-like rows newest-first: `created_at` descending, ties broken
 * by `id` descending. Shared by {@link sortQuotesForDisplay} and the offer-group
 * ordering in {@link groupQuotesByOffer} so the two can't drift apart.
 *
 * The `id` tiebreak matters in practice: a fanned-out extraction writes all of an
 * email's date options in one transaction, so they share a `created_at`.
 */
function compareQuotesNewestFirst(a: SortableQuote, b: SortableQuote): number {
  const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
  const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
  if (dateA !== dateB) return dateB - dateA;
  return b.id - a.id;
}

/**
 * Orders quotes newest-first: `created_at` descending, ties broken by `id`
 * descending.
 *
 * A venue can hold several active quotes at once — one per `quote_group_id`, each a
 * distinct concurrent offer — so "the quote for this venue" is no longer a single row
 * the database hands back. Most surfaces still show one figure per venue, and without
 * an explicit order they would each pick whichever row the query happened to return
 * first, which can differ between two renders of the same page. Sorting at the point
 * where quotes are loaded makes every downstream `find` deterministic whether or not
 * it goes through {@link selectHeadlineQuote}.
 *
 * @param quotes - Quotes to order. Not mutated.
 * @returns A new array, newest first.
 */
export function sortQuotesForDisplay<T extends SortableQuote>(quotes: readonly T[]): T[] {
  return [...quotes].sort(compareQuotesNewestFirst);
}

/**
 * Picks the one active quote that represents a venue on surfaces with room for a
 * single figure — a proposal card's price, a funnel bucket, a package total.
 *
 * This is a display convention, not a statement about the data: when a venue has
 * several live offers, the newest one stands in for all of them until the surface
 * learns to show more than one. Callers that need a different offer pass a
 * `predicate` rather than reaching past this function, so every surface resolves
 * ties the same way.
 *
 * Archived quotes are assumed to have been filtered out already — `getVenueOptions`
 * and `getVenueOption` both drop them at the boundary.
 *
 * @param quotes - The venue's quotes. Order does not matter; this sorts internally.
 * @param predicate - Optional extra condition, applied on top of `is_active`.
 * @returns The headline quote, or undefined when no active quote qualifies.
 */
export function selectHeadlineQuote<T extends SortableQuote>(
  quotes: readonly T[] | null | undefined,
  predicate?: (quote: T) => boolean
): T | undefined {
  const eligible = (quotes ?? []).filter((quote) => quote.is_active === true && (predicate?.(quote) ?? true));
  return sortQuotesForDisplay(eligible)[0];
}

/**
 * Picks the active quote that best represents a venue when status matters: a
 * negotiated price beats an estimate, and an estimate beats anything else still live.
 *
 * The hierarchy encodes a product decision about which number to put in front of
 * someone, so it lives here rather than being restated at each surface that needs it.
 * Callers with extra fallbacks of their own (reaching past active quotes entirely,
 * say) layer those on top of this rather than re-expressing the first three rungs.
 *
 * @param quotes - The venue's quotes. Order does not matter.
 * @returns The preferred active quote, or undefined when none is active.
 */
export function selectPreferredActiveQuote<T extends SortableQuote & { status?: string }>(
  quotes: readonly T[] | null | undefined
): T | undefined {
  return (
    selectHeadlineQuote(quotes, (quote) => quote.status === OeeQuoteStatus.Negotiated) ??
    selectHeadlineQuote(quotes, (quote) => quote.status === OeeQuoteStatus.Estimated) ??
    selectHeadlineQuote(quotes)
  );
}

/** The minimum a quote-like row needs to be grouped into offers. */
export interface GroupableQuote extends SortableQuote {
  quote_group_id: string;
}

/** One offer: a `quote_group_id` and the quotes filed under it, newest first. */
export interface QuoteOfferGroup<T> {
  quoteGroupId: string;
  /** All quotes in this offer, newest first (see {@link sortQuotesForDisplay}). */
  quotes: T[];
  /** The quote that represents this offer — see {@link selectHeadlineQuote}. */
  headline: T;
}

/**
 * Groups a venue's quotes into offers (`quote_group_id`) — one group per distinct
 * offer, each internally ordered newest-first.
 *
 * Groups are themselves ordered by the same rule {@link selectHeadlineQuote} uses to
 * pick a single quote: newest active quote wins, falling back to the group's newest
 * quote of any status when an offer has no active quote left. This guarantees offer 1
 * of N always contains whichever quote `selectHeadlineQuote(quotes)` would return for
 * the whole venue, so a surface that used to collapse to one card keeps showing the
 * same quote as offer 1 once it renders N cards instead.
 *
 * @param quotes - The venue's quotes, all offers mixed together. Order does not matter.
 * @returns Offer groups, newest-headline-first.
 */
export function groupQuotesByOffer<T extends GroupableQuote>(quotes: readonly T[]): QuoteOfferGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const quote of quotes) {
    const existing = groups.get(quote.quote_group_id);
    if (existing) existing.push(quote);
    else groups.set(quote.quote_group_id, [quote]);
  }

  const offers = Array.from(groups.entries()).map(([quoteGroupId, groupQuotes]) => {
    const sorted = sortQuotesForDisplay(groupQuotes);
    const headline = sorted.find((quote) => quote.is_active === true) ?? sorted[0];
    return { quoteGroupId, quotes: sorted, headline };
  });

  return offers.sort((a, b) => compareQuotesNewestFirst(a.headline, b.headline));
}

/**
 * Scopes quotes to one offer when requested, but falls back to the full,
 * unscoped list — the same default-selection behavior as omitting a
 * `quoteGroupId` entirely — rather than an empty result when the offer id is
 * stale or invalid (e.g. it was since archived). Callers pipe the result into
 * whichever single-quote selector they use — {@link selectHeadlineQuote} here,
 * or `ava-add-quotes`'s `selectQuotesToDisplay`.
 *
 * @param quotes - The full set of quotes to scope.
 * @param quoteGroupId - The offer to scope to, or null/undefined for none.
 * @returns The offer's quotes, or every quote if none matched a real offer.
 */
export function scopeToOffer<T extends GroupableQuote>(quotes: readonly T[], quoteGroupId?: string | null): T[] {
  if (!quoteGroupId) return [...quotes];
  const offerQuotes = quotes.filter((quote) => quote.quote_group_id === quoteGroupId);
  return offerQuotes.length > 0 ? offerQuotes : [...quotes];
}

/**
 * Verifies a quote id or an offer (venue + `quote_group_id`) actually belongs to
 * the claimed event, directly against `oee_quotes` — the only place either can be
 * validated, since neither `oee_quotes.id` nor `quote_group_id` has a parent
 * table a real foreign key could enforce this through.
 *
 * @param supabase - Client to read with (service or authenticated — `oee_quotes`
 *   reads are broadly permitted either way).
 * @param eventId - The event the caller claims this quote/offer belongs to.
 * @param match - A specific quote id (optionally also scoped to a venue), or an offer scoped to a venue.
 * @returns Whether a matching row exists.
 */
export async function quoteMatchesEvent(
  supabase: SupabaseClient,
  eventId: number,
  match: { quoteId: number; venueStaticId?: number } | { quoteGroupId: string; venueStaticId: number }
): Promise<boolean> {
  let query = supabase.schema("nowadays").from("oee_quotes").select("id").eq("event_id", eventId);
  if ("quoteId" in match) {
    query = query.eq("id", match.quoteId);
    if (match.venueStaticId !== undefined) {
      query = query.eq("venue_static_id", match.venueStaticId);
    }
  } else {
    query = query.eq("venue_static_id", match.venueStaticId).eq("quote_group_id", match.quoteGroupId);
  }

  const { data, error } = await query.limit(1).maybeSingle();
  if (error) {
    console.error("quoteMatchesEvent:", { eventId, match }, error);
    return false;
  }
  return data != null;
}

/**
 * Whether `quoteGroupId` is already in use by a *different* event/venue than claimed.
 * A group id that doesn't exist yet in `oee_quotes` at all is fine — that's the normal
 * case of minting a fresh offer (`createQuote` with no existing group, or copying a
 * venue's quotes into a new event with remapped group ids). Every row sharing one
 * `quote_group_id` belongs to the same event+venue by construction, so checking one
 * row is enough to catch a caller-supplied id that actually belongs elsewhere.
 *
 * @param supabase - Client to read with.
 * @param quoteGroupId - The offer id a write is about to use.
 * @param eventId - The event the caller claims this offer belongs to.
 * @param venueStaticId - The venue the caller claims this offer belongs to.
 * @returns True if `quoteGroupId` exists and belongs to a different event/venue; false
 *   if it doesn't exist yet, or already belongs to this exact event+venue. Fails closed
 *   (returns true) on a read error, since the caller can't verify safety either way.
 */
export async function quoteGroupBelongsToOther(
  supabase: SupabaseClient,
  quoteGroupId: string,
  eventId: number,
  venueStaticId: number
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("oee_quotes")
    .select("event_id, venue_static_id")
    .eq("quote_group_id", quoteGroupId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("quoteGroupBelongsToOther:", { quoteGroupId, eventId, venueStaticId }, error);
    return true;
  }
  if (!data) return false;
  return data.event_id !== eventId || data.venue_static_id !== venueStaticId;
}

/**
 * Whether two values are equal, treating `null` and `undefined` as the same "unset".
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns Whether the two are equal under that rule.
 */
function nullableEquals(a: unknown, b: unknown): boolean {
  return (a ?? null) === (b ?? null);
}

/**
 * Filters a query on a column, using `.is()` for `null`/`undefined` and `.eq()`
 * otherwise — Postgres treats `column = NULL` as never-true, so the two cases need
 * different operators even though they express the same "matches this value" rule.
 *
 * @param query - The Postgrest filter builder to extend.
 * @param column - The column to filter on.
 * @param value - The value to match, or `null`/`undefined` to match an unset column.
 * @returns The extended query.
 */
function eqOrIsNull<T extends { eq: (c: string, v: unknown) => T; is: (c: string, v: null) => T }>(
  query: T,
  column: string,
  value: unknown
): T {
  return value == null ? query.is(column, null) : query.eq(column, value);
}

/**
 * A quote's room block, reduced to the fields that decide whether two quotes offer
 * the same rooms: the rate rows, order-independent (a re-extraction can emit them in
 * a different order for what is otherwise the same offer). Other `Guestrooms` fields
 * — notes, taxes, fees, total_cost — are detail rather than what defines the block,
 * so a difference there alone shouldn't stop two submissions from being "the same
 * offer" the way a different rate or room type should.
 *
 * Each rate is reduced to a fixed-order tuple rather than stringifying the object
 * directly: the match side is a plain JS object built fresh on every call, while the
 * DB side comes back from a `jsonb` column, whose storage format does not preserve
 * object key order. `JSON.stringify` on the two would very likely disagree on key
 * order for identical data, silently defeating this match on the exact resubmission
 * case this function exists to catch. A tuple's order comes from this code, not from
 * either object's own key order, so it's immune to that.
 *
 * @param guestrooms - The guestrooms block to reduce, if any.
 * @returns A string safe to compare with `===` for room-block equality.
 */
function roomBlockKey(guestrooms?: Guestrooms | null): string {
  // Sorting by each tuple's own JSON encoding (rather than a delimited string) means
  // two structurally different tuples can never tie in the comparator — a delimiter
  // like "|" could otherwise appear inside a vendor-supplied room `name` and collide
  // across tuples, which a stable sort would then resolve by original input order,
  // reopening exactly the order-instability this function exists to close.
  const rates = (guestrooms?.room_rates ?? [])
    // Normalize amount/count to numbers before comparing: this repo's own form always
    // produces numbers, but oee_quotes rows can also be written by pipelines outside
    // this repo, and a numeric string on one side (`"200"`) would otherwise never
    // equal a number on the other (`200`), silently missing a real match.
    .map((rate) => JSON.stringify([rate.date, rate.name, Number(rate.amount), Number(rate.count)]))
    .sort();
  return JSON.stringify(rates);
}

/**
 * A quote's meeting-room block, reduced the same way {@link roomBlockKey} reduces
 * guestrooms — sorted, fixed-order rate tuples — but also capturing whether a
 * meeting room was quoted *at all*, not just its cost. Unlike a hotel room block,
 * a meeting room is genuinely optional, and a waived/comped one ($0 `total_cost`,
 * empty `room_rates`) contributes the same $0 to `grand_total` as no meeting room
 * being quoted at all — so price alone can't tell "with meeting room" and
 * "without" apart, even though a caller who quoted one considers it a different
 * offer than one who didn't.
 *
 * @param meetingRooms - The meeting-rooms block to reduce, if any.
 * @returns A string safe to compare with `===` for meeting-room equality.
 */
function meetingRoomKey(meetingRooms?: MeetingRooms | null): string {
  if (!meetingRooms) return JSON.stringify(null);
  const rates = (meetingRooms.room_rates ?? [])
    .map((rate) => JSON.stringify([rate.date, rate.name, Number(rate.amount)]))
    .sort();
  return JSON.stringify({ present: true, rates });
}

/**
 * Finds a non-archived quote already on file for the same event/venue/dates whose
 * commercial terms — currency, grand total, sourcing fee, room block, meeting-room
 * block — exactly match the quote about to be inserted. `createQuote` uses this to
 * fold a form resubmission (double
 * click, retry) into the existing offer instead of opening a second, independently
 * active one for what is really the same quote.
 *
 * Matching stops at dates alone on purpose: a venue can legitimately offer two
 * different room blocks for the same dates, and those need to stay separate offers.
 * Only a quote whose terms are identical too counts as the same offer.
 *
 * This is a best-effort, read-then-write check, not a guarantee — it doesn't close
 * the window between two genuinely concurrent submissions (both read no match, both
 * insert). Closing that fully needs a DB-level uniqueness constraint, which belongs
 * in the migrations repo, not here.
 *
 * This also duplicates, in application code, what the DB's `assign_quote_group()` is
 * meant to do when `quote_group_id` is left null on insert — `createQuote` never
 * leaves it null, so that DB-side path never runs for this table. The two are not
 * guaranteed to agree; reconciling them (or retiring one) is tracked as a follow-up
 * in the migrations repo rather than done here, since that function lives outside
 * this repo.
 *
 * On a query error, this fails *open* — it logs and returns undefined, same as "no
 * match found" — rather than blocking the write like {@link quoteGroupBelongsToOther}
 * does. That's deliberate: a transient read failure here should cost an occasional
 * duplicate offer (the pre-existing, already-tolerated failure mode), not a hard
 * failure on the quote-creation the caller actually asked for.
 *
 * @param supabase - Client to read with.
 * @param match - The event, venue, dates, and terms of the quote about to be inserted.
 * @returns The matching quote's `quote_group_id`, or undefined if none matches.
 * @throws Never — a query error is logged and treated as "no match found" (see above).
 */
export async function findMatchingQuoteGroupId(
  supabase: SupabaseClient,
  match: {
    eventId: number;
    venueStaticId: number;
    startDate?: string | null;
    endDate?: string | null;
    currency?: string | null;
    grandTotal?: number | null;
    sourcingFeePercent?: number | null;
    guestrooms?: Guestrooms | null;
    meetingRooms?: MeetingRooms | null;
  }
): Promise<string | undefined> {
  // The whole body is wrapped, not just the query's resolved `{ error }` case: a
  // caller can hand this a client that doesn't implement every filter method this
  // uses (a hand-rolled test stub, an older client version), which throws rather
  // than resolving with an error. Fail-open has to hold against that too, or a
  // caller relying on this never rejecting can break in a way "check the error
  // field" alone wouldn't catch.
  try {
    let query = supabase
      .schema("nowadays")
      .from("oee_quotes")
      .select("quote_group_id, extraction_data")
      .eq("event_id", match.eventId)
      .eq("venue_static_id", match.venueStaticId)
      .is("archived_at", null);
    query = eqOrIsNull(query, "grand_total", match.grandTotal);
    query = eqOrIsNull(query, "sourcing_fee_percent", match.sourcingFeePercent);

    // More than one non-archived quote can match exactly (e.g. a re-quote at the
    // same price after an earlier one stalled) — order newest-first and cap the
    // scan so which offer gets reused is deterministic and bounded rather than an
    // unlimited fetch over whatever order Postgres happens to return.
    const { data, error } = await query.order("id", { ascending: false }).limit(50);

    if (error) {
      console.error("findMatchingQuoteGroupId:", match, error);
      return undefined;
    }

    const roomBlock = roomBlockKey(match.guestrooms);
    const meetingRoomBlock = meetingRoomKey(match.meetingRooms);
    const existing = (data ?? []).find(
      (row) =>
        nullableEquals(row.extraction_data?.start_date, match.startDate) &&
        nullableEquals(row.extraction_data?.end_date, match.endDate) &&
        nullableEquals(row.extraction_data?.currency, match.currency) &&
        roomBlockKey(row.extraction_data?.guestrooms) === roomBlock &&
        meetingRoomKey(row.extraction_data?.meeting_rooms) === meetingRoomBlock
    );

    return existing?.quote_group_id;
  } catch (error) {
    console.error("findMatchingQuoteGroupId:", match, error);
    return undefined;
  }
}

/**
 * Raw `is_active` write shared by {@link activateQuote} and {@link deactivateQuote}.
 * No caller-authorization check of its own — callers own that.
 *
 * @param supabase - Client to write with (service client — bypasses `oee_quotes`
 *   RLS, so callers own authorization).
 * @param quoteId - The quote to update.
 * @param isActive - The value to write.
 * @throws If the update fails.
 */
async function setQuoteActiveState(supabase: SupabaseClient, quoteId: number, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .schema("nowadays")
    .from("oee_quotes")
    .update({ is_active: isActive })
    .eq("id", quoteId);
  if (error) {
    console.error("setQuoteActiveState:", { quoteId, isActive }, error);
    throw new Error(`Failed to ${isActive ? "activate" : "deactivate"} quote`);
  }
}

/**
 * Sets a quote active — shared by `setQuoteAsActive` (ava-add-quotes/_logic/actions/update.ts,
 * gated behind `authorizeQuoteMutation`) and `createQuote`'s own internal
 * activation, which is covered by `createQuote`'s token-flow gate instead.
 *
 * @param supabase - Client to write with (service client — bypasses `oee_quotes`
 *   RLS, so callers own authorization).
 * @param quoteId - The quote to activate.
 * @throws If the update fails.
 */
export async function activateQuote(supabase: SupabaseClient, quoteId: number): Promise<void> {
  await setQuoteActiveState(supabase, quoteId, true);
}

/**
 * Sets a quote inactive — shared by `setQuoteAsInactive` (ava-add-quotes/_logic/actions/update.ts,
 * gated behind `authorizeQuoteMutation`).
 *
 * Unlike {@link activateQuote}, this never triggers `trigger_enforce_single_active_quote`
 * (schema.sql) — that trigger only fires `WHEN (new.is_active = true)` — so it's a plain,
 * uncontested write. Leaves `archived_at`/`archived_by` untouched: deactivating is not
 * archiving.
 *
 * @param supabase - Client to write with (service client — bypasses `oee_quotes`
 *   RLS, so callers own authorization).
 * @param quoteId - The quote to deactivate.
 * @throws If the update fails.
 */
export async function deactivateQuote(supabase: SupabaseClient, quoteId: number): Promise<void> {
  await setQuoteActiveState(supabase, quoteId, false);
}
